import { useNavigation, CommonActions } from '@react-navigation/native';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Platform } from 'react-native';

import useActiveChat, { ActiveChat } from '@hooks/useActiveChat';
import useSearch from '@hooks/useSearch';
import { AppStackScreenProps } from '@navigation/app-stack';
import { ChatsStackParamList } from '@navigation/chats/chats-stack';
export enum ChatDrawerScreen {
  closed,
  details,
  settingsIndividual,
  settingsGroup,
  newChat,
  newGroupChat,
  completeTaskModal,
  assignTaskModal,
  upgradeTeamModal,
}

type ChatDrawerScreenNavMap = Record<
  ChatDrawerScreen,
  keyof ChatsStackParamList | undefined
> & { [ChatDrawerScreen.closed]: undefined };
const chatDrawerScreenNavMap: ChatDrawerScreenNavMap = {
  [ChatDrawerScreen.closed]: undefined,
  [ChatDrawerScreen.details]: 'chat-detail',
  [ChatDrawerScreen.settingsIndividual]: 'individual-chat-settings',
  [ChatDrawerScreen.settingsGroup]: 'group-chat-settings',
  [ChatDrawerScreen.newChat]: 'new-chat',
  [ChatDrawerScreen.newGroupChat]: 'create-group-stack',
  [ChatDrawerScreen.completeTaskModal]: 'task-modal',
  [ChatDrawerScreen.assignTaskModal]: 'task-modal',
  [ChatDrawerScreen.upgradeTeamModal]: 'upgrade-team-modal',
};

type WebDrawerContext = {
  screen: ChatDrawerScreen;
  navigateToScreen: (screen: ChatDrawerScreen) => void;
  chatExpanded: boolean;
  setChatExpanded: (chatExpanded: boolean) => void;
  toggleChatExpanded: () => void;
  isBottomBannerShow: boolean;
  setIsBottomBannerShow: (isBottomBannerShow: boolean) => void;
  chatDrawerWidth: number;
  setChatDrawerWidth: (width: number) => void;
  chatListOpen: boolean;
  setChatListOpen: (open: boolean) => void;
  toggleChatListOpen: () => void;
  shouldChatListOpen: boolean;
  setShouldChatListOpen: (open: boolean) => void;
  isCreateProjectOpen: boolean;
  setIsCreateProjectOpen: (isBottomBannerShow: boolean) => void;
  isEditProfileOpen: boolean;
  setIsEditProfileOpen: (open: boolean) => void;
  editProjectUUId: string;
  setEditProjectUUId: (editProjectUUId: string) => void;
  editContactId: string;
  setEditContactId: (editContactId: string) => void;
  isTaskWebPanelOpen: boolean;
  setIsTaskWebPanelOpen: (isBottomBannerShow: boolean) => void;
  idProjectChecklistTemplateOpen: string;
  setIdProjectChecklistTemplateOpen: (
    idProjectChecklistTemplateOpen: string
  ) => void;
  idProjectSaveChecklistAsTemplateOpen: string;
  setIdProjectSaveChecklistAsTemplateOpen: (
    idProjectChecklistTemplateOpen: string
  ) => void;
  isTaskWebPanelMaximize: boolean;
  setIsTaskWebPanelMaximize: (a: boolean) => void;
  idTaskDetailOpen: string;
  setIdTaskDetailOpen: (idtask: string) => void;
  idProjectOfTaskDetailOpen: string;
  setIdProjectOfTaskDetailOpen: (idproject: string) => void;
  uuidProjectOfTaskDetailOpen: string;
  setUUIdProjectOfTaskDetailOpen: (idproject: string) => void;
  taskNameOfTaskDetailOpen: string;
  setTaskNameOfTaskDetailOpen: (name: string) => void;
  projectNameOfTaskDetailOpen: string;
  setProjectNameOfTaskDetailOpen: (name: string) => void;
  mainPanelWidth: number;
  setMainPanelWidth: (width: number) => void;
  isUpgradeTeamPlanOpen: boolean;
  idTeamDelete: string;
  idTeamOpen: string;
  isTeamPersonal: boolean;
  setIdTeamOpen: (id: string) => void;
  setIdTeamDelete: (id: string) => void;
  uuidTeamOpen: string;
  setUUIdTeamOpen: (id: string) => void;
  setIsTeamPersonal: (a: boolean) => void;
  setUpgradeCTA: (bool: boolean) => void;
  setUpgradeTeamPlanOpen: (isOpen: boolean) => void;
  setTeamValues: (a: any) => void;
  teamValues: any;
  setIsNewTeam: (i: boolean) => void;
  isNewTeam: boolean;
  upgradeCTA: boolean;
  isContactWebPanelOpen: boolean;
  setIsContactWebPanelOpen: (isBottomBannerShow: boolean) => void;
  idContactDetailOpen: string;
  setIdContactDetailOpen: (idContact: string) => void;
  setUserInvited: (value: boolean) => void;
  userInvited: boolean;
  navigateToTeamCreated: () => void;
  subscriptionCreatedModalOpen: boolean;
  setSubscriptionCreatedModalOpen: (open: boolean) => void;
  subscriptionCreatedTeamUUId: string;
  setSubscriptionCreatedTeamUUId: (id: string) => void;
  upgradeConfirmModalOpen: boolean;
  setUpgradeConfirmModalOpen: (open: boolean) => void;
  editTeamConfirmModalOpen: boolean;
  setEditTeamConfirmModalOpen: (open: boolean) => void;
  editTeamConfirmTeam: Team | undefined;
  setEditTeamConfirmTeam: (team: Team | undefined) => void;
  editTeamConfirmOnContinue: (() => void) | undefined;
  setEditTeamConfirmOnContinue: (fn: (() => void) | undefined) => void;
  editSubscriptionAutoAnnual: boolean;
  setEditSubscriptionAutoAnnual: (value: boolean) => void;
  onDropFile: ((files: File[]) => Promise<void>) | undefined;
  setOnDropFile: React.Dispatch<
    React.SetStateAction<((files: File[]) => Promise<void>) | undefined>
  >;
  paywallModalOpen: boolean;
  setPaywallModalOpen: (open: boolean) => void;
  paywallTeam: Team | undefined;
  setPaywallTeam: (team: Team | undefined) => void;
  showPaywallTeamSetup: boolean;
  setShowPaywallTeamSetup: (open: boolean) => void;
  showPaywallInviteMembers: boolean;
  setShowPaywallInviteMembers: (open: boolean) => void;
  showPaywallSubscriptionModal: boolean;
  setShowPaywallSubscriptionModal: (open: boolean) => void;
  joinedProject: { id: string; uuid: string; name: string } | null;
  setJoinedProject: (
    project: { id: string; uuid: string; name: string } | null
  ) => void;
  showProjectCreatedModal: boolean;
  setShowProjectCreatedModal: (show: boolean) => void;
  projectCreatedName: string;
  setProjectCreatedName: (name: string) => void;
};

const initialWebDrawerContext: WebDrawerContext = {
  screen: ChatDrawerScreen.closed,
  navigateToScreen: () => void 0,
  chatExpanded: false,
  setChatExpanded: () => void 0,
  navigateToTeamCreated: () => void 0,
  toggleChatExpanded: () => void 0,
  isBottomBannerShow: true,
  setIsBottomBannerShow: () => void 0,
  chatDrawerWidth: 0,
  setChatDrawerWidth: () => void 0,
  chatListOpen: true,
  setChatListOpen: () => void 0,
  toggleChatListOpen: () => void 0,
  shouldChatListOpen: false,
  setShouldChatListOpen: () => void 0,
  isCreateProjectOpen: false,
  setIsCreateProjectOpen: () => void 0,
  isEditProfileOpen: false,
  setIsEditProfileOpen: () => void 0,
  editProjectUUId: '',
  setEditProjectUUId: () => void 0,
  isTaskWebPanelOpen: false,
  setIsTaskWebPanelOpen: () => void 0,
  idProjectChecklistTemplateOpen: '',
  setIdProjectChecklistTemplateOpen: () => void 0,
  idProjectSaveChecklistAsTemplateOpen: '',
  setIdProjectSaveChecklistAsTemplateOpen: () => void 0,
  isTaskWebPanelMaximize: false,
  setIsTaskWebPanelMaximize: () => void 0,
  idTaskDetailOpen: '',
  setIdTaskDetailOpen: () => void 0,
  idProjectOfTaskDetailOpen: '',
  setIdProjectOfTaskDetailOpen: () => void 0,
  uuidProjectOfTaskDetailOpen: '',
  setUUIdProjectOfTaskDetailOpen: () => void 0,
  taskNameOfTaskDetailOpen: '',
  setTaskNameOfTaskDetailOpen: () => void 0,
  projectNameOfTaskDetailOpen: '',
  setProjectNameOfTaskDetailOpen: () => void 0,
  mainPanelWidth: 580,
  setMainPanelWidth: () => void 0,
  isUpgradeTeamPlanOpen: false,
  setUpgradeTeamPlanOpen: () => void 0,
  idTeamOpen: '0',
  idTeamDelete: '0',
  setIdTeamOpen: () => void 0,
  setIdTeamDelete: () => void 0,
  uuidTeamOpen: '0',
  setUUIdTeamOpen: () => void 0,
  setIsTeamPersonal: () => void 0,
  setUpgradeCTA: () => void 0,
  isTeamPersonal: false,
  upgradeCTA: false,
  setTeamValues: () => void 0,
  teamValues: undefined,
  setIsNewTeam: () => void 0,
  isNewTeam: false,
  isContactWebPanelOpen: false,
  setIsContactWebPanelOpen: () => void 0,
  idContactDetailOpen: '',
  setIdContactDetailOpen: () => void 0,
  editContactId: '',
  setEditContactId: () => void 0,
  subscriptionCreatedModalOpen: false,
  setSubscriptionCreatedModalOpen: () => void 0,
  subscriptionCreatedTeamUUId: '',
  setSubscriptionCreatedTeamUUId: () => void 0,
  upgradeConfirmModalOpen: false,
  setUpgradeConfirmModalOpen: () => void 0,
  editTeamConfirmModalOpen: false,
  setEditTeamConfirmModalOpen: () => void 0,
  editTeamConfirmTeam: undefined,
  setEditTeamConfirmTeam: () => void 0,
  editTeamConfirmOnContinue: undefined,
  setEditTeamConfirmOnContinue: () => void 0,
  editSubscriptionAutoAnnual: false,
  setEditSubscriptionAutoAnnual: () => void 0,
  onDropFile: undefined,
  setOnDropFile: () => {},
  paywallModalOpen: false,
  setPaywallModalOpen: () => void 0,
  paywallTeam: undefined,
  setPaywallTeam: () => void 0,
  showPaywallTeamSetup: false,
  setShowPaywallTeamSetup: () => void 0,
  showPaywallInviteMembers: false,
  setShowPaywallInviteMembers: () => void 0,
  showPaywallSubscriptionModal: false,
  setShowPaywallSubscriptionModal: () => void 0,
  joinedProject: null,
  setJoinedProject: () => void 0,
  showProjectCreatedModal: false,
  setShowProjectCreatedModal: () => void 0,
  projectCreatedName: '',
  setProjectCreatedName: () => void 0,
};

const WebDrawerContext = createContext<WebDrawerContext>(
  initialWebDrawerContext
);

/**
 * Provides state and manipulation for web chat drawers
 */
export const WebDrawerProvider = ({
  children,
  screen: screenProp,
}: {
  children: ReactNode;
  /** The screen to load for the drawer */
  screen?: ChatDrawerScreen;
}) => {
  const navigation = useNavigation();
  const [chatListOpen, setChatListOpen] = useState(true);
  const [chatExpanded, setChatExpanded] = useState(false);
  const [screen, setScreen] = useState(screenProp || ChatDrawerScreen.closed);
  const [isBottomBannerShow, setIsBottomBannerShow] = useState(false);
  const [chatDrawerWidth, setChatDrawerWidth] = useState(0);
  const [shouldChatListOpen, setShouldChatListOpen] = useState(false);
  const [isCreateProjectOpen, setIsCreateProjectOpen] = useState(false);
  const [isEditProfileOpen, setIsEditProfileOpen] = useState(false);
  const [editProjectUUId, setEditProjectUUId] = useState('');
  const [editContactId, setEditContactId] = useState('');

  const [isTaskWebPanelOpen, setIsTaskWebPanelOpen] = useState(false);

  const [isTaskWebPanelMaximize, setIsTaskWebPanelMaximize] = useState(false);
  const [idTaskDetailOpen, setIdTaskDetailOpen] = useState('');
  const [idContactDetailOpen, setIdContactDetailOpen] = useState('');
  const [idProjectOfTaskDetailOpen, setIdProjectOfTaskDetailOpen] =
    useState('');
  const [uuidProjectOfTaskDetailOpen, setUUIdProjectOfTaskDetailOpen] =
    useState('');
  const [taskNameOfTaskDetailOpen, setTaskNameOfTaskDetailOpen] = useState('');
  const [projectNameOfTaskDetailOpen, setProjectNameOfTaskDetailOpen] =
    useState('');
  const [mainPanelWidth, setMainPanelWidth] = useState(0);
  const [isUpgradeTeamPlanOpen, setUpgradeTeamPlanOpen] = useState(false);

  const [isContactWebPanelOpen, setIsContactWebPanelOpen] = useState(false);

  const [teamValues, setTeamValues] = useState<any>(undefined);
  const [isNewTeam, setIsNewTeam] = useState(false);
  const [idTeamOpen, setIdTeamOpen] = useState('');
  const [idTeamDelete, setIdTeamDelete] = useState('');
  const [uuidTeamOpen, setUUIdTeamOpen] = useState('');
  const [isTeamPersonal, setIsTeamPersonal] = useState(false);
  const [upgradeCTA, setUpgradeCTA] = useState(false);
  const [userInvited, setUserInvited] = useState(false);
  const [subscriptionCreatedModalOpen, setSubscriptionCreatedModalOpen] =
    useState(false);
  const [subscriptionCreatedTeamUUId, setSubscriptionCreatedTeamUUId] =
    useState('');
  const [upgradeConfirmModalOpen, setUpgradeConfirmModalOpen] = useState(false);
  const [editTeamConfirmModalOpen, setEditTeamConfirmModalOpen] =
    useState(false);
  const [editTeamConfirmTeam, setEditTeamConfirmTeam] = useState<
    Team | undefined
  >(undefined);
  const [editTeamConfirmOnContinue, setEditTeamConfirmOnContinue] = useState<
    (() => void) | undefined
  >(undefined);
  const [editSubscriptionAutoAnnual, setEditSubscriptionAutoAnnual] =
    useState(false);
  const [onDropFile, setOnDropFile] = useState<
    ((files: File[]) => Promise<void>) | undefined
  >(undefined);
  const [paywallModalOpen, setPaywallModalOpen] = useState(false);
  const [paywallTeam, setPaywallTeam] = useState<Team | undefined>(undefined);
  const [showPaywallTeamSetup, setShowPaywallTeamSetup] = useState(false);
  const [showPaywallInviteMembers, setShowPaywallInviteMembers] =
    useState(false);
  const [showPaywallSubscriptionModal, setShowPaywallSubscriptionModal] =
    useState(false);
  const [joinedProject, setJoinedProject] = useState<{
    id: string;
    uuid: string;
    name: string;
  } | null>(null);
  const [showProjectCreatedModal, setShowProjectCreatedModal] = useState(false);
  const [projectCreatedName, setProjectCreatedName] = useState('');

  const [idProjectChecklistTemplateOpen, setIdProjectChecklistTemplateOpen] =
    useState('');
  const [
    idProjectSaveChecklistAsTemplateOpen,
    setIdProjectSaveChecklistAsTemplateOpen,
  ] = useState('');

  useEffect(() => {
    screenProp && setScreen(screenProp);
  }, [screenProp]);

  const navigateToTeamCreated = (teamUUId: string) => {
    navigation.navigate('my-account', {
      screen: 'subscriptionPlanDetail',
      params: {
        teamUUId: teamUUId,
      },
    });
  };

  useEffect(() => {
    async function getScreenInfo() {
      const info = window.sessionStorage.getItem('screenInfo');
      if (info) {
        const c = JSON.parse(info);
        setScreen(c.screen);
        setChatExpanded(c.chatExpanded);
        setIsBottomBannerShow(c.isBottomBannerShow);
        setChatDrawerWidth(c.chatDrawerWidth);
        setChatListOpen(c.chatListOpen);
        setShouldChatListOpen(c.shouldChatListOpen);
        setEditContactId(c.editContactId);
        setIsTaskWebPanelOpen(c.isTaskWebPanelOpen);
        setIdProjectChecklistTemplateOpen('');
        setIdProjectSaveChecklistAsTemplateOpen('');
        setIdTaskDetailOpen(c.idTaskDetailOpen);
        setIdContactDetailOpen(c.idContactDetailOpen);
        setIsTaskWebPanelMaximize(c.isTaskWebPanelMaximize);
        setMainPanelWidth(c.mainPanelWidth);
        setIdProjectOfTaskDetailOpen(c.idProjectOfTaskDetailOpen);
        setUUIdProjectOfTaskDetailOpen(c.uuidProjectOfTaskDetailOpen);
      }
    }
    getScreenInfo();
  }, []);

  useEffect(() => {
    // https://tasktag.atlassian.net/browse/TA-4012
    // 5. When the page is reloaded, everything is reset: the sidebar opens from a collapsed state, the task closes, the chat opens the main screen
    function saveScreenInfo() {
      const info = {
        screen,
        chatExpanded,
        isBottomBannerShow,
        chatDrawerWidth,
        chatListOpen,
        shouldChatListOpen,
        editContactId,
        isTaskWebPanelOpen,
        idTaskDetailOpen,
        idContactDetailOpen,
        isTaskWebPanelMaximize,
        mainPanelWidth,
        idProjectOfTaskDetailOpen,
        uuidProjectOfTaskDetailOpen,
      };
      window.sessionStorage.setItem('screenInfo', JSON.stringify(info));
    }

    saveScreenInfo();
  }, [
    screen,
    chatExpanded,
    isBottomBannerShow,
    chatDrawerWidth,
    chatListOpen,
    shouldChatListOpen,
    editContactId,
    isTaskWebPanelOpen,
    idTaskDetailOpen,
    idContactDetailOpen,
    isTaskWebPanelMaximize,
    mainPanelWidth,
    idProjectOfTaskDetailOpen,
    uuidProjectOfTaskDetailOpen,
  ]);

  const handleSetIdTaskDetailOpen = useCallback((idtask: string) => {
    setIdTaskDetailOpen(idtask);
    // TA-10467: the task/project name fallback (used to render a
    // "Request to Join" panel while the current user has no access to the
    // task) must not outlive the task it was set for — otherwise closing
    // this task and opening another via a path that never sets a name
    // (e.g. a task share link) shows/acts on the previous task's stale name.
    if (!idtask) {
      setTaskNameOfTaskDetailOpen('');
      setProjectNameOfTaskDetailOpen('');
    }
  }, []);

  const toggleChatListOpen = () => {
    setChatListOpen((o) => !o);
  };

  const toggleChatExpanded = () => {
    setChatExpanded((o) => !o);
  };

  const value = useMemo(
    () => ({
      navigateToTeamCreated: navigateToTeamCreated,
      screen,
      navigateToScreen: setScreen,
      chatExpanded,
      setChatExpanded,
      toggleChatExpanded,
      isBottomBannerShow,
      setIsBottomBannerShow,
      chatDrawerWidth,
      setChatDrawerWidth,
      chatListOpen,
      setChatListOpen,
      toggleChatListOpen,
      shouldChatListOpen,
      setShouldChatListOpen,
      isCreateProjectOpen,
      setIsCreateProjectOpen,
      isEditProfileOpen,
      setIsEditProfileOpen,
      editProjectUUId,
      setEditProjectUUId,
      editContactId,
      setEditContactId,
      isTaskWebPanelOpen,
      setIsTaskWebPanelOpen,
      idProjectChecklistTemplateOpen,
      setIdProjectChecklistTemplateOpen,
      idProjectSaveChecklistAsTemplateOpen,
      setIdProjectSaveChecklistAsTemplateOpen,
      isTaskWebPanelMaximize,
      setIsTaskWebPanelMaximize,
      idTaskDetailOpen,
      setIdTaskDetailOpen: handleSetIdTaskDetailOpen,
      idProjectOfTaskDetailOpen,
      setIdProjectOfTaskDetailOpen,
      uuidProjectOfTaskDetailOpen,
      setUUIdProjectOfTaskDetailOpen,
      taskNameOfTaskDetailOpen,
      setTaskNameOfTaskDetailOpen,
      projectNameOfTaskDetailOpen,
      setProjectNameOfTaskDetailOpen,
      mainPanelWidth,
      setMainPanelWidth,
      isUpgradeTeamPlanOpen,
      setUpgradeTeamPlanOpen,
      idTeamOpen,
      idTeamDelete,
      setIdTeamOpen,
      setIdTeamDelete,
      uuidTeamOpen,
      setUUIdTeamOpen,
      upgradeCTA,
      isTeamPersonal,
      setIsTeamPersonal,
      setUpgradeCTA,
      teamValues,
      setTeamValues,
      isNewTeam,
      setIsNewTeam,
      userInvited,
      setUserInvited,
      isContactWebPanelOpen,
      setIsContactWebPanelOpen,
      idContactDetailOpen,
      setIdContactDetailOpen,
      subscriptionCreatedModalOpen,
      setSubscriptionCreatedModalOpen,
      subscriptionCreatedTeamUUId,
      setSubscriptionCreatedTeamUUId,
      upgradeConfirmModalOpen,
      setUpgradeConfirmModalOpen,
      editTeamConfirmModalOpen,
      setEditTeamConfirmModalOpen,
      editTeamConfirmTeam,
      setEditTeamConfirmTeam,
      editTeamConfirmOnContinue,
      setEditTeamConfirmOnContinue,
      editSubscriptionAutoAnnual,
      setEditSubscriptionAutoAnnual,
      onDropFile,
      setOnDropFile,
      paywallModalOpen,
      setPaywallModalOpen,
      paywallTeam,
      setPaywallTeam,
      showPaywallTeamSetup,
      setShowPaywallTeamSetup,
      showPaywallInviteMembers,
      setShowPaywallInviteMembers,
      showPaywallSubscriptionModal,
      setShowPaywallSubscriptionModal,
      joinedProject,
      setJoinedProject,
      showProjectCreatedModal,
      setShowProjectCreatedModal,
      projectCreatedName,
      setProjectCreatedName,
    }),
    [
      screen,
      navigateToTeamCreated,
      chatExpanded,
      isBottomBannerShow,
      chatDrawerWidth,
      chatListOpen,
      shouldChatListOpen,
      isCreateProjectOpen,
      isEditProfileOpen,
      editProjectUUId,
      editContactId,
      setEditContactId,
      isTaskWebPanelOpen,
      idProjectChecklistTemplateOpen,
      idProjectSaveChecklistAsTemplateOpen,
      idTaskDetailOpen,
      handleSetIdTaskDetailOpen,
      isTaskWebPanelMaximize,
      mainPanelWidth,
      idProjectOfTaskDetailOpen,
      uuidProjectOfTaskDetailOpen,
      taskNameOfTaskDetailOpen,
      projectNameOfTaskDetailOpen,
      isUpgradeTeamPlanOpen,
      setUpgradeTeamPlanOpen,
      idTeamOpen,
      idTeamDelete,
      setIdTeamDelete,
      setIdTeamOpen,
      uuidTeamOpen,
      setUUIdTeamOpen,
      upgradeCTA,
      isTeamPersonal,
      setIsTeamPersonal,
      setUpgradeCTA,
      teamValues,
      setTeamValues,
      isNewTeam,
      setIsNewTeam,
      userInvited,
      setUserInvited,
      isContactWebPanelOpen,
      idContactDetailOpen,
      setIdContactDetailOpen,
      subscriptionCreatedModalOpen,
      subscriptionCreatedTeamUUId,
      upgradeConfirmModalOpen,
      editTeamConfirmModalOpen,
      editTeamConfirmTeam,
      editTeamConfirmOnContinue,
      editSubscriptionAutoAnnual,
      setOnDropFile,
      paywallModalOpen,
      paywallTeam,
      showPaywallTeamSetup,
      showPaywallInviteMembers,
      showPaywallSubscriptionModal,
      joinedProject,
      showProjectCreatedModal,
      projectCreatedName,
    ]
  );

  return (
    <WebDrawerContext.Provider value={value}>
      {children}
    </WebDrawerContext.Provider>
  );
};

export const useWebDrawer = () => {
  const context = useContext(WebDrawerContext);

  if (!context) {
    throw new Error(
      'useWebDrawerContext can only be used within a WebDrawerProvider'
    );
  }

  return context;
};

/**
 * Returns an object containing the state of the web drawer.
 * @returns Object containing the state of the web drawer.
 */
export const useWebDrawerState = () => {
  const {
    screen,
    chatExpanded,
    isBottomBannerShow,
    chatDrawerWidth,
    chatListOpen,
    shouldChatListOpen,
  } = useWebDrawer();

  return {
    screen,
    chatExpanded,
    isBottomBannerShow,
    chatDrawerWidth,
    chatListOpen,
    shouldChatListOpen,
  };
};

/**
 * Allows navigation to either the chat details drawer or general navigation.
 *
 * When navigating to a details screen, do not set an active chat. This is handled internally.
 *
 * @returns A method that allows navigation to the chat details drawer or general navigation.
 */
export const useNavigateToChatDrawer = (): ((
  options:
    | {
        screen: Exclude<
          ChatDrawerScreen,
          | ChatDrawerScreen.details
          | ChatDrawerScreen.completeTaskModal
          | ChatDrawerScreen.assignTaskModal
        >;
        activeChat?: never;
        activeMessageId?: string;
        chatId?: never;
        messageId?: string;
        resetStack?: boolean;
      }
    | (
        | {
            screen:
              | ChatDrawerScreen.details
              | ChatDrawerScreen.completeTaskModal
              | ChatDrawerScreen.assignTaskModal;
            activeChat?: never;
            activeMessageId?: string;
            messageId?: string;
            chatId: ActiveChat['id'];
            resetStack?: boolean;
          }
        | {
            screen:
              | ChatDrawerScreen.details
              | ChatDrawerScreen.completeTaskModal
              | ChatDrawerScreen.assignTaskModal;
            activeChat: ActiveChat;
            activeMessageId?: string;
            chatId?: string;
            messageId?: string;
            resetStack?: boolean;
          }
      )
) => void) => {
  const context = useContext(WebDrawerContext);
  const navigation =
    useNavigation<AppStackScreenProps<'chats-stack'>['navigation']>();

  const { setActiveChat, setActiveMessageId } = useActiveChat();
  const { setIsSearching } = useSearch();

  // TODO: Migrate this inside the context such that we do not need to expose the
  // `context.navigateToScreen` method.
  return ({
    screen,
    activeChat,
    activeMessageId,
    messageId,
    chatId,
    resetStack,
  }) => {
    if (context && Platform.OS === 'web') {
      setIsSearching(false);
      activeChat && setActiveChat(activeChat);
      activeMessageId && setActiveMessageId?.(activeMessageId);
      context.navigateToScreen(screen);
    } else if (screen !== ChatDrawerScreen.closed) {
      const navScreen = chatDrawerScreenNavMap[screen];

      if (navScreen) {
        if (screen === ChatDrawerScreen.completeTaskModal) {
          navigation.navigate('chats-stack', {
            screen: 'task-modal',
            params: { complete: true },
          });
        } else if (!resetStack) {
          // TODO: Fix type error
          navigation.navigate('chats-stack', {
            screen: navScreen,
            params: {
              id: chatId ?? activeChat?.id,
              activeMessageId: activeMessageId,
              messageId,
            },
          });
        } else {
          navigation.dispatch(
            CommonActions.reset({
              index: 0,
              routes: [
                {
                  name: 'chat-detail',
                  params: {
                    id: chatId ?? activeChat?.id,
                    activeMessageId: activeMessageId,
                    messageId,
                  },
                },
              ],
            })
          );
        }
      }
    }
  };
};
